import { Vue, Component, Inject, Prop, Watch, Emit } from 'vue-property-decorator'
import { Validator } from 'vee-validate'
import { StatisticalAnalysisCommon, StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { initialStatisticalAnalysisSidebarSearchByState, StatisticalAnalysisSearchBySidebarEventEnum, StatisticalAnalysisSidebarSearchByModel, StatisticalAnalysisSidebarSearchByState } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-search-by/statistical-analysis-sidebar-search-by.model'
import { isArray } from 'lodash'
import { commonSimpleListEntity } from '@/api/resources/common/simple-list-entities/simple-list-entities.service'
import { SimpleListEntitiesAllowedEnum, SimpleListEntitiesResponse, SimpleListEntitiesResponseContent } from '@/api/resources/common/simple-list-entities/simple-list-entities.dto'
import { statisticalAnalysisSimpleListEntity } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-simple-list/statistical-analysis-simple-list.service'
import { StatisticalAnalysisSimpleListEntitiesAllowedEnum } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-simple-list/statistical-analysis-simple-list.dto'
import { StatiscalAnalysisTypeCases } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { Formatter } from '@/filters/formatter'

@Component
export default class StatisticalAnalysisSidebarSearchBy extends Vue implements StatisticalAnalysisCommon<StatisticalAnalysisSidebarSearchByModel> {
    @Prop({ default: true }) readonly showTypeSearch!: boolean
    @Prop({ default: false }) readonly filterByStates!: boolean
    @Prop({ default: false }) readonly filterByEntityTypes!: boolean
    @Prop() readonly options!: StatisticalAnalysisSidebarItemCombo[]
    @Prop() readonly preselection!: StatisticalAnalysisSidebarItemCombo | StatisticalAnalysisSidebarItemCombo[]
    @Prop({ default: false }) readonly multiple!: boolean
    @Inject() externalValidator!: Validator
    @Emit()
    finish(): boolean {
        return true
    }
    @Watch('options', { immediate: true, deep: true })
    onOptionsChanged(newOptions: StatisticalAnalysisSidebarItemCombo[]) {
        if (newOptions) {
            this.state.typeSearch.list = newOptions
        }
    }
    @Watch('preselection', { immediate: true, deep: true })
    onPreselectionChanged(newPreselection: StatisticalAnalysisSidebarItemCombo) {
        if (newPreselection) {
            this.state.typeSearch.selection = newPreselection
        }
    }

    state: StatisticalAnalysisSidebarSearchByState
    constructor() {
        super()
        this.state = initialStatisticalAnalysisSidebarSearchByState()
        this.$validator = this.externalValidator
    }

    get entityTypeLabel():string {
        const entitySelected = (this.state.typeSearch.selection as StatisticalAnalysisSidebarItemCombo).text
        return Formatter.capitalize(this.$tc('statisticalAnalysis.dossiers.entityType', undefined, { entityType: this.$tc((entitySelected)) }))
    }

    async mounted() {
        if (!isArray(this.state.typeSearch.selection)) {
            if (this.filterByStates) {
                await this.onInputTypeSearch(this.state.typeSearch.selection as StatisticalAnalysisSidebarItemCombo)
            }
            if (this.filterByEntityTypes) {
                await this.onInputTypeSearch(this.state.typeSearch.selection as StatisticalAnalysisSidebarItemCombo)
            }
            this.finish()
        } else {
            this.finish()
        }
    }

    clear(): void {
        this.state = {
            ...this.state,
            states: {
                ...this.state.states,
                selection: this.state.states.list
            },
            entityTypes: {
                ...this.state.entityTypes,
                selection: []
            },
            typeSearch: {
                ...this.state.typeSearch,
                selection: this.preselection
            }
        }
    }

    getModel(): StatisticalAnalysisSidebarSearchByModel {
        if (this.filterByStates) {
            return {
                typeSearch: this.state.typeSearch.selection!,
                states: this.state.states.selection!
            }
        } else if (this.filterByEntityTypes) {
            return {
                typeSearch: this.state.typeSearch.selection!,
                typeIds: this.state.entityTypes.selection!
            }
        } else {
            return {
                typeSearch: this.state.typeSearch.selection!
            }
        }
    }

    getTranslatedLabel(item: StatisticalAnalysisSidebarItemCombo): string {
        return this.$tc(item.text, 2)
    }

    async onInputTypeSearch(value: StatisticalAnalysisSidebarItemCombo | StatisticalAnalysisSidebarItemCombo[]): Promise<void> {
        this.state.typeSearch.selection = value
        if (!isArray(value)) {
            this.$eventHub.$emit(StatisticalAnalysisSearchBySidebarEventEnum.SEARCH_BY_ENTITY_CHANGE, value)
            if (this.filterByStates) {
                await this._updateStates(value.id)
            } else {
                this.state.states.list = []
            }
            if (this.filterByEntityTypes) {
                await this._updateEntityTypes(value.id)
            } else {
                this.state.entityTypes.list = []
            }
        }
    }

    onInputStates(value: StatisticalAnalysisSidebarItemCombo[]): void {
        this.state.states.selection = value
    }

    onEntityTypes(value: StatisticalAnalysisSidebarItemCombo[]): void {
        this.state.entityTypes.selection = value
    }

    private async _updateStates(selection: string): Promise<void> {
        switch (selection) {
        case StatiscalAnalysisTypeCases.CASE:
            await this._parseStates(statisticalAnalysisSimpleListEntity.getListOfEntity(StatisticalAnalysisSimpleListEntitiesAllowedEnum.CASE_VIRTUAL_STATE))
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            await this._parseStates(commonSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.SAMPLE_STATES))
            break
        }
    }

    private async _updateEntityTypes(selection: string): Promise<void> {
        switch (selection) {
        case StatiscalAnalysisTypeCases.CASE:
            await this._parseEntityTypes(statisticalAnalysisSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.CASE_TYPE))
            break
        case StatiscalAnalysisTypeCases.SAMPLE:
            await this._parseEntityTypes(statisticalAnalysisSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.SAMPLE_TYPE))
            break
        case StatiscalAnalysisTypeCases.PORTION:
            await this._parseEntityTypes(statisticalAnalysisSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.PORTION_TYPE))
            break
        case StatiscalAnalysisTypeCases.SUBPORTION:
            await this._parseEntityTypes(statisticalAnalysisSimpleListEntity.getListOfEntity(SimpleListEntitiesAllowedEnum.SUBPORTION_TYPE))
            break
        }
    }

    private async _parseStates(service: Promise<SimpleListEntitiesResponse>): Promise<void> {
        const [error, response] = await this.$_toCall(service)
        if (error) {
            this.state.states.list = []
        }

        this.state.states.list = response?.content?.map((item: SimpleListEntitiesResponseContent) => {
            return {
                id: item.id,
                text: item.label
            }
        })
        this.state.states.selection = this.state.states.list
    }

    private async _parseEntityTypes(service: Promise<SimpleListEntitiesResponse>): Promise<void> {
        const [error, response] = await this.$_toCall(service)
        if (error) {
            this.state.entityTypes.list = []
        }

        this.state.entityTypes.list = response?.content?.map((item: SimpleListEntitiesResponseContent) => {
            return {
                id: item.id,
                text: item.label
            }
        })
        this.state.entityTypes.selection = []
    }
}
